import { useState } from "react";

const RFQSection = () => {
  const [form, setForm] = useState({ productName: "", category: "", quantity: "", targetPrice: "", dropshipping: false, companyName: "", contactPerson: "", email: "", phone: "", country: "", message: "", verifiedOnly: false });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    setForm((prev) => ({ ...prev, [target.name]: target.type === "checkbox" ? target.checked : target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, String(v)));
      await fetch("https://readdy.ai/api/form/d7ejulrfimgqccpmabn0", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  return (
    <section id="rfq" className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-amber-500 text-xs font-semibold uppercase tracking-widest mb-3">Request for Quotation</p>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Get Quotes from Verified Suppliers</h2>
          <p className="text-gray-500 mt-3 text-base">Receive competitive quotes within 24 hours from verified Middle East factories</p>
        </div>
        {submitted ? (
          <div className="max-w-lg mx-auto text-center py-16 bg-amber-50 rounded-2xl">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4"><i className="ri-checkbox-circle-fill text-3xl text-amber-500"></i></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">RFQ Submitted!</h3>
            <p className="text-gray-500 text-sm">Verified suppliers will respond within 24 hours. Check your email for updates.</p>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="max-w-4xl mx-auto bg-amber-50 rounded-2xl p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2"><i className="ri-box-3-line text-amber-500"></i> Product Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Name / Description</label>
                    <textarea name="productName" value={form.productName} onChange={handleChange} rows={3} placeholder="Describe the product you need..." className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white resize-none" required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Category</label>
                      <select name="category" value={form.category} onChange={handleChange} className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                        <option value="">Select...</option>
                        <option>Electronics</option><option>Textiles</option><option>Machinery</option><option>Food &amp; Beverage</option><option>Furniture</option><option>Beauty</option><option>Construction</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Quantity Needed</label>
                      <input name="quantity" value={form.quantity} onChange={handleChange} type="number" placeholder="e.g. 500" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Target Price (USD per unit)</label>
                    <input name="targetPrice" value={form.targetPrice} onChange={handleChange} type="text" placeholder="e.g. $5 - $10" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="dropshipping" checked={form.dropshipping} onChange={handleChange} className="w-4 h-4 accent-amber-500" />
                    <span className="text-sm text-gray-700">Dropshipping required</span>
                  </label>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2"><i className="ri-user-line text-amber-500"></i> Your Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Company Name</label>
                    <input name="companyName" value={form.companyName} onChange={handleChange} type="text" placeholder="Your company name" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" required />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Contact Person</label>
                    <input name="contactPerson" value={form.contactPerson} onChange={handleChange} type="text" placeholder="Full name" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" required />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Email Address</label>
                    <input name="email" value={form.email} onChange={handleChange} type="email" placeholder="your@email.com" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" required />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number</label>
                    <input name="phone" value={form.phone} onChange={handleChange} type="tel" placeholder="+971 50 000 0000" className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Country / Region</label>
                    <select name="country" value={form.country} onChange={handleChange} className="w-full border border-amber-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                      <option value="">Select country...</option>
                      <option>UAE</option><option>Saudi Arabia</option><option>Qatar</option><option>USA</option><option>UK</option><option>Germany</option><option>China</option><option>India</option><option>Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Additional Requirements <span className="text-gray-400 font-normal">(max 500 chars)</span></label>
              <textarea name="message" value={form.message} onChange={handleChange} rows={3} maxLength={500} placeholder="Any specific requirements, certifications, packaging needs..." className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white resize-none" />
              <div className="text-right text-xs text-gray-400 mt-1">{form.message.length}/500</div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer mt-3 mb-6">
              <input type="checkbox" name="verifiedOnly" checked={form.verifiedOnly} onChange={handleChange} className="w-4 h-4 accent-amber-500" />
              <span className="text-sm text-gray-700">Send to verified suppliers only</span>
            </label>
            <button type="submit" disabled={submitting || form.message.length > 500} className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-4 rounded-lg text-base transition-colors cursor-pointer whitespace-nowrap">
              {submitting ? "Submitting..." : "Submit RFQ — Get Quotes in 24 Hours"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default RFQSection;
